import * as THREE from "three";
import { playerController } from "three-player-controller";
import Orchestrator from "../Orchestrator.js";
import {
    CONFIG,
    columnFromX,
    columnFromZ,
    columnX,
    columnZ,
    surfaceY,
} from "./config.js";

const PLAYER_SCALE = 1 / 180;
const PLAYER_CAPSULE_RADIUS_RATIO = 1.8;
const PLAYER_CAPSULE_GROUND_HEIGHT =
    (180 - 30 * PLAYER_CAPSULE_RADIUS_RATIO) * PLAYER_SCALE;

export default class PlayerController {
    constructor({ input, terrainCollider, model }) {
        this.orc = new Orchestrator();
        this.scene = this.orc.scene;
        this.camera = this.orc.camera;
        this.debug = this.orc.debug;
        this.input = input;
        this.terrainCollider = terrainCollider;
        this.model = model;

        this.instance = new playerController();
        this.colliderHandle = null;
        this.position = new THREE.Vector3();
        this.spawnPosition = new THREE.Vector3();
        this.state = {
            i: 0,
            j: 0,
            digs: 0,
        };
        this.initialized = false;
        this.isGrounded = false;
        this.debugParams = {
            showCapsule: false,
            showCollisionMesh: false,
        };
    }

    get terrain() {
        return this.terrainCollider.terrain;
    }

    async init() {
        this.setSpawnPosition();
        await this.instance.init({
            scene: this.scene,
            camera: this.camera.instance,
            controls: this.camera.controls,
            playerModelConfig: {
                model: this.model,
                animations: [],
                scale: PLAYER_SCALE,
                idleAnim: "",
                walkAnim: "",
                runAnim: "",
                jumpAnim: "",
                speed: CONFIG.PLAYER_SPEED,
                capsuleRadiusRatio: PLAYER_CAPSULE_RADIUS_RATIO,
            },
            initPos: this.getSpawnCapsulePosition(),
            mouseSensitivity: 20,
            minCamDistance: 10,
            maxCamDistance: 1500,
            thirdMouseMode: 3,
            enableZoom: true,
            isShowMobileControls: false,
            keyMap: {
                forward: null,
                backward: null,
                left: null,
                right: null,
                sprint: null,
                jump: null,
                toggleView: null,
                toggleFly: null,
                toggleVehicle: null,
            },
        });

        this.replaceTerrainCollider(this.terrainCollider.collisionMesh);
        this.initialized = true;
        this.setDebug();
    }

    setSpawnPosition() {
        const spawn = this.terrain.layout?.spawn ?? {
            x: CONFIG.WORLD_SIZE / 2,
            z: CONFIG.WORLD_SIZE / 2,
        };
        const i = spawn.x | 0;
        const j = spawn.z | 0;
        const height = this.terrain.heightAt(i, j);

        this.state.i = i;
        this.state.j = j;
        this.spawnPosition.set(columnX(i), surfaceY(height), columnZ(j));
    }

    getSpawnCapsulePosition() {
        return this.spawnPosition.clone().setY(
            this.spawnPosition.y + PLAYER_CAPSULE_GROUND_HEIGHT,
        );
    }

    replaceTerrainCollider(collisionMesh) {
        const nextHandle = this.instance.addCollider({
            motion: "static",
            shape: {
                kind: "mesh",
                mesh: collisionMesh,
            },
        });
        const previousHandle = this.colliderHandle;

        this.colliderHandle = nextHandle;
        if (previousHandle) this.instance.removeCollider(previousHandle);
    }

    setDebug() {
        if (!this.debug.active) return;

        const folder = this.debug.ui.addFolder("Player Controller");
        folder
            .add(this.debugParams, "showCapsule")
            .name("show capsule")
            .onChange((visible) => {
                this.instance.setPlayerCapsuleDebug(visible);
            });
        folder
            .add(this.debugParams, "showCollisionMesh")
            .name("show collision mesh")
            .onChange((visible) => {
                this.instance.setColliderDebug(visible);
            });
        folder
            .add(CONFIG, "PLAYER_SPEED", 300, 5000, 10)
            .name("move speed")
            .onChange((speed) => {
                this.instance.setPlayerSpeed(speed);
            });
    }

    syncPosition() {
        const capsulePosition = this.instance.getPosition();
        if (!capsulePosition) return;

        this.position.copy(capsulePosition);
        this.position.y -= this.instance.getCapsuleGroundHeight();
    }

    updateState() {
        this.state.i = THREE.MathUtils.clamp(
            columnFromX(this.position.x),
            0,
            CONFIG.WORLD_SIZE - 1,
        );
        this.state.j = THREE.MathUtils.clamp(
            columnFromZ(this.position.z),
            0,
            CONFIG.WORLD_SIZE - 1,
        );
    }

    refreshColliderIfNeeded() {
        if (!this.initialized) return;
        if (!this.terrainCollider.needsRebuild(this.state.i, this.state.j)) return;

        const mesh = this.terrainCollider.rebuild(this.state.i, this.state.j);
        this.replaceTerrainCollider(mesh);
    }

    reset() {
        if (!this.initialized) return;

        this.setSpawnPosition();
        this.instance.reset(this.getSpawnCapsulePosition());
        this.instance.isupdate = true;
        this.instance.setInput({ moveX: 0, moveY: 0 });

        this.state.digs = 0;
        this.isGrounded = false;
        this.syncPosition();
        this.updateState();
    }

    setEnabled(enabled) {
        this.instance.isupdate = enabled;
        if (!enabled) {
            this.instance.setInput({ moveX: 0, moveY: 0 });
            this.instance.playerVelocity.set(0, 0, 0);
        }
    }

    update(dt) {
        if (!this.initialized || !this.instance.isupdate) return;

        const input = this.input.readMoveInput();
        this.instance.setInput({
            moveX: input.strafe,
            moveY: input.forward,
        });
        void this.instance.update(dt);

        this.isGrounded = this.instance.getIsOnGround();
        this.syncPosition();
        this.updateState();
        this.refreshColliderIfNeeded();
    }
}
